/**
 * SessionPicker — header combobox for switching, renaming and deleting PRATUS
 * AI chat sessions. Replaces the legacy list/chat view toggle: the current
 * session name is the trigger, the list opens in a popover with a filter box.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  MessageSquareIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { filterSessions, type ChatSessionSummary } from './chatHelpers';

export { filterSessions, type ChatSessionSummary } from './chatHelpers';

export interface SessionPickerProps {
  sessions: ChatSessionSummary[];
  isLoading?: boolean;
  currentId?: string;
  currentName?: string;
  onSelect: (session: ChatSessionSummary) => void;
  onRename: (id: string, name: string) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
}

const SessionPicker: React.FC<SessionPickerProps> = ({
  sessions,
  isLoading = false,
  currentId,
  currentName,
  onSelect,
  onRename,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<ChatSessionSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<ChatSessionSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(
    () => filterSessions(sessions, filter),
    [sessions, filter]
  );

  const label = currentId ? currentName || 'Untitled chat' : 'New chat';

  const handleRename = async () => {
    if (!renaming) return;
    const name = renameValue.trim();
    if (!name) return;
    setBusy(true);
    try {
      await onRename(renaming.id, name);
      toast.success('Chat renamed.');
      setRenaming(null);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await onDelete(deleting.id);
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              className="tw:min-w-0 tw:max-w-full tw:justify-start tw:gap-1.5 tw:px-2"
              aria-label="Switch chat session"
              data-testid="ai-session-picker"
            />
          }
        >
          <MessageSquareIcon className="tw:shrink-0 tw:opacity-70" />
          <span className="tw:truncate tw:font-medium" title={label}>
            {label}
          </span>
          <ChevronDownIcon className="tw:shrink-0 tw:opacity-60" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="dark tw:w-80 tw:gap-2 tw:p-2"
          aria-label="Chat sessions"
        >
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter chats by name"
            aria-label="Filter chats by name"
            autoFocus
          />
          <ScrollArea className="tw:max-h-72">
            {isLoading ? (
              <div className="tw:flex tw:flex-col tw:gap-2 tw:p-1">
                <Skeleton className="tw:h-8 tw:w-full" />
                <Skeleton className="tw:h-8 tw:w-full" />
                <Skeleton className="tw:h-8 tw:w-3/4" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="tw:px-2 tw:py-4 tw:text-center tw:text-sm tw:text-muted-foreground">
                {filter ? 'No chats match.' : 'No chats yet.'}
              </p>
            ) : (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-0.5 tw:p-0">
                {filtered.map((session) => {
                  const active = session.id === currentId;
                  return (
                    <li
                      key={session.id}
                      className={cn(
                        'tw:group/session tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:pr-1',
                        active ? 'tw:bg-accent' : 'tw:hover:bg-accent/60'
                      )}
                    >
                      <button
                        type="button"
                        className="tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:truncate tw:rounded-md tw:border-0 tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:text-foreground"
                        title={session.name}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => {
                          onSelect(session);
                          setOpen(false);
                        }}
                      >
                        {session.name}
                      </button>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              aria-label={`Rename ${session.name}`}
                              onClick={() => {
                                setRenaming(session);
                                setRenameValue(session.name);
                              }}
                            />
                          }
                        >
                          <PencilIcon />
                        </TooltipTrigger>
                        <TooltipContent className="dark">Rename</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              aria-label={`Delete ${session.name}`}
                              onClick={() => setDeleting(session)}
                            />
                          }
                        >
                          <Trash2Icon />
                        </TooltipTrigger>
                        <TooltipContent className="dark">Delete</TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="dark">
          <DialogHeader>
            <DialogTitle>Edit Chat Name</DialogTitle>
            <DialogDescription>
              Rename this PRATUS AI chat session.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-label="Chat name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleRename();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleRename()}
              disabled={busy || !renameValue.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Delete this PRATUS AI Chat session? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={busy}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SessionPicker;
